/**
 * Meta (Facebook) Pixel for the public website.
 * Loads only after analytics consent (same gate as PostHog).
 */

import { getAnalyticsConsent } from './consent'

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    _fbq?: (...args: unknown[]) => void
  }
}

const PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined)?.trim() || ''

let booted = false

export function getMetaPixelId(): string {
  return PIXEL_ID
}

export function isMetaPixelConfigured(): boolean {
  return /^\d{5,20}$/.test(PIXEL_ID)
}

function canRun(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false
  if (!isMetaPixelConfigured()) return false
  if (getAnalyticsConsent() !== 'accepted') return false
  return true
}

/** Inject Meta Pixel base + init + PageView (once). */
export function initMetaPixel(): boolean {
  if (!canRun()) return false
  if (booted && typeof window.fbq === 'function') {
    return true
  }

  // Standard Meta snippet (idempotent).
  const f = window
  const b = document
  if (!f.fbq) {
    const n = (f.fbq = function (...args: unknown[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fn = n as any
      if (fn.callMethod) fn.callMethod(...args)
      else fn.queue.push(args)
    }) as typeof window.fbq & {
      callMethod?: (...a: unknown[]) => void
      queue: unknown[]
      push: typeof Array.prototype.push
      loaded: boolean
      version: string
    }
    if (!f._fbq) f._fbq = n
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nn = n as any
    nn.push = nn.push || Array.prototype.push
    nn.loaded = true
    nn.version = '2.0'
    nn.queue = []
    const t = b.createElement('script')
    t.async = true
    t.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const s = b.getElementsByTagName('script')[0]
    s?.parentNode?.insertBefore(t, s)
  }

  window.fbq?.('init', PIXEL_ID)
  window.fbq?.('track', 'PageView')
  booted = true
  return true
}

export function trackMetaPageView(): void {
  if (!canRun()) return
  if (!booted) {
    initMetaPixel()
    return
  }
  window.fbq?.('track', 'PageView')
}

/** Open Live Account / Sign up CTA — Meta Lead. */
export function trackMetaLead(extra?: Record<string, string>): void {
  if (!canRun()) return
  if (!booted) initMetaPixel()
  window.fbq?.('track', 'Lead', extra || {})
}

/** Register intent — CompleteRegistration when user hits create-account CTA. */
export function trackMetaCompleteRegistration(extra?: Record<string, string>): void {
  if (!canRun()) return
  if (!booted) initMetaPixel()
  window.fbq?.('track', 'CompleteRegistration', extra || {})
}

export function __resetMetaPixelForTests(): void {
  booted = false
}
